import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './LocalOrders.css';

interface Order {
  id: number;
  order_number: string;
  status: string;
  pickup_method: string;
  subtotal: string;
  total: string;
  created_at: string;
  locker_passcode?: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: '#f5a623' },
  awaiting_payment: { label: 'Awaiting Payment', color: '#f5a623' },
  paid: { label: 'Preparing', color: '#2d5a27' },
  ready_for_pickup: { label: 'Ready for Pickup', color: '#2d5a27' },
  picked_up: { label: 'Picked Up', color: '#666' },
  cancelled: { label: 'Cancelled', color: '#dc3545' },
};

const LocalOrders: React.FC = () => {
  const navigate = useNavigate();
  const { hasLocalAccess } = useLocalAccess();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    loadOrders();
  }, [hasLocalAccess, navigate]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getLocalOrders();
      setOrders(data || []);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!hasLocalAccess) {
    return null;
  }

  return (
    <BridgeClubLayout>
      <div className="local-orders">
        <div className="orders-header">
          <h1>Order History</h1>
          <Link to="/bridge-club/shop" className="shop-link">
            Shop Now
          </Link>
        </div>

        {loading ? (
          <div className="orders-loading">
            <div className="spinner"></div>
            <p>Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="orders-empty">
            <p>No orders yet</p>
            <Link to="/bridge-club/shop" className="shop-link-btn">
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map((order) => {
              const statusInfo = STATUS_LABELS[order.status] || {
                label: order.status,
                color: '#666',
              };
              return (
                <Link
                  key={order.id}
                  to={`/bridge-club/local-orders/${order.id}`}
                  className="order-card"
                >
                  <div className="order-main">
                    <div className="order-number">{order.order_number}</div>
                    <div className="order-date">{formatDate(order.created_at)}</div>
                  </div>
                  <div className="order-details">
                    <span
                      className="order-status"
                      style={{ background: statusInfo.color }}
                    >
                      {statusInfo.label}
                    </span>
                    <span className="order-pickup">
                      {order.pickup_method === 'locker' ? 'Locker' : 'In-Store'}
                    </span>
                    <span className="order-total">${order.total}</span>
                  </div>
                  {order.status === 'ready_for_pickup' &&
                    order.pickup_method === 'locker' &&
                    order.locker_passcode && (
                      <div className="order-locker-code">
                        Locker Code: <strong>{order.locker_passcode}</strong>
                      </div>
                    )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default LocalOrders;
